import React, { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IconMinus, IconPlus } from "@tabler/icons-react";
import { Slider } from "antd";
import { View } from "@/styles/reactnative";
import { ThemeContext } from "../../../../../../../theme/ThemeProvider";
import { CButton } from "../../../../../../components/Common";
import {
  CrossModalLeverage,
  CrossTradeAmount,
} from "./styles";
import styled from "styled-components";
import { media } from "@/styles/Qtheme";

const CrossSliderBar = ({ leverage, setLeverage, maxLeverage }) => {
  const { t } = useTranslation();
  const { textw } = useContext(ThemeContext).theme.COLORS;
  const STEP = 1;

  const { theme } = useContext(ThemeContext);

  // antd Slider onChange (value를 직접 받음)
  const handleSliderChange = (value: number) => {
    if (value >= 1 && value <= maxLeverage) {
      setLeverage(value);
    }
  };

  // input onChange (event 기반)
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;

    if (newValue === "") {
      setLeverage(1);
    } else {
      const parsedValue = parseFloat(newValue);
      if (!isNaN(parsedValue) && parsedValue >= 1 && parsedValue <= maxLeverage) {
        setLeverage(parsedValue);
      }
    }
  };

  const incrementLeverage = () => {
    const max = maxLeverage;
    const val = leverage + 1;
    if (val > max) {
      setLeverage(max);
    } else {
      setLeverage(val);
    }
  };

  const decrementLeverage = () => {
    const min = 1;
    const val = leverage - 1;
    if (val < min) {
      setLeverage(1);
    } else {
      setLeverage(val);
    }
  };

  // 슬라이더 마크 생성 (1x, 25x, 50x, 75x, 100x, 125x)
  const sliderMarks = useMemo(() => {
    const marks: Record<number, React.ReactNode | { style?: React.CSSProperties; label: React.ReactNode }> = { 1: '1x' };
    const buttonIncrement = 25;
    const numButtons = Math.floor(maxLeverage / buttonIncrement);

    for (let i = 1; i <= numButtons; i++) {
      const value = i * buttonIncrement;
      const isLast = value === numButtons * buttonIncrement;

      if (isLast) {
        // 마지막 마크: 오른쪽 정렬 (50% - 4px 이동)
        marks[value] = {
          style: {
            transform: 'translateX(calc(-50% - 10px))',
          },
          label: `${value}x`,
        };
      } else {
        marks[value] = `${value}x`;
      }
    }

    return marks;
  }, [maxLeverage]);

  return (
    <View
      style={{
        alignItems: "flex-start",
        width: "100%",
      }}
    >
      <CrossModalLeverage>
        <div
          style={{
            fontSize: 13,
            fontFamily: 'NanumSquareRound',
            fontWeight: 400,
            color: '#b3b5bd',
            letterSpacing: '-0.36px'
          }}
        >
          {t("leverage")}
        </div>
        <CrossTradeAmount>
          <CButton
            icon={<IconMinus size={16} color="#b3b5bd" />}
            onClick={decrementLeverage}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 20,
              height: 20,
              padding: 0,
              cursor: "pointer",
            }}
          />
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
            <LeverageInput
              value={leverage}
              maxLength={4}
              onChange={handleInputChange}
              onClick={(event: any) => {
                event.target.select();
              }}
              style={{
                width: `${String(leverage).length}ch`,
              }}
            />
            <span
              style={{
                color: '#ffffff',
                fontWeight: 700,
                fontSize: 16,
                fontFamily: 'NanumSquareRound',
                letterSpacing: '-0.32px'
              }}
            >
              x
            </span>
          </View>
          <CButton
            icon={<IconPlus size={16} color="#b3b5bd" />}
            onClick={incrementLeverage}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 20,
              height: 20,
              padding: 0,
              cursor: "pointer",
            }}
          />
        </CrossTradeAmount>
      </CrossModalLeverage>

      <SliderWrapper>
        <Slider
          min={1}
          max={maxLeverage}
          value={leverage}
          onChange={handleSliderChange}
          step={STEP}
          marks={sliderMarks}
          tooltip={{ open: false }}
          styles={{
            rail: {
              background: '#2A2C2E',
              height: 3,
            },
            track: {
              background: '#07D993',
              height: 3,
            },
            handle: {
              border: `1px solid #07D993`,
              color: 'black',
              background: '#07D993',
            },
          }}
        />
      </SliderWrapper>
    </View>
  );
};

export default CrossSliderBar;

const LeverageInput = styled.input`
  color: #ffffff;
  background: transparent;
  font-weight: 700;
  font-size: 16px;
  font-family: 'NanumSquareRound';
  border: 0;
  outline: none;
  text-align: right;
  letter-spacing: -0.32px;
  padding: 0;
  margin: 0;
  min-width: 1ch;
  max-width: 4ch;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;

const SliderWrapper = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 0 2px;
  margin-top: 20px;

  .ant-slider-mark-text {
    font-size: 12px;
    font-family: 'NanumSquareRound';
    font-weight: 400;
    color: #fff;
    letter-spacing: '-0.36px';
    top: 4px;

    @media ${media.mobile} {
      font-size: 11px;
    }
  }

  @media ${media.mobile} {
    margin-top: 10px;
  }
`;
